import { useCallback, useMemo, useState } from 'react';
import { useProjectIceFacade, useStartupPageFacade } from '@ideoz/core';
import type { ScopeStatus } from '@ideoz/core';
import { useScopesList } from './useScopesList';

const KNOWN_STATUSES: ScopeStatus[] = ['planned', 'in_progress', 'blocked', 'completed'];

/**
 * Logic for the Work Scopes module: owns the server-driven scopes list
 * (pagination + search + sort via `useScopesList`), the create/edit-scope
 * actions (via the shared startup facade), the project's ICE scores, and the
 * delete-confirmation + ICE-details dialog state.
 */
export function useWorkScopes(params: {
  startupId: string;
  navigateToScope: (scopeId: string) => void;
}) {
  // The list is driven by `useScopesList`, so tell the facade not to also fetch
  // the Redux scopes array (it would double-hit the endpoint on this page).
  const facade = useStartupPageFacade({
    navigateToScope: params.navigateToScope,
    startupId: params.startupId,
    fetchScopes: false,
  });

  const list = useScopesList(params.startupId);

  // Reading scores never computes one, so this is safe on every load.
  const ice = useProjectIceFacade({ projectId: params.startupId });

  const [scopeToDelete, setScopeToDelete] = useState<{ id: string; title: string } | null>(null);
  const [iceScopeId, setIceScopeId] = useState<string | null>(null);

  /**
   * The current page mapped to the card shape. The composite ICE prefers the
   * value the backend now returns on the row, falling back to the live ICE
   * facade so a freshly-queued "Scoring…" still resolves in place.
   */
  const scopesWithIce = useMemo(
    () =>
      list.scopes.map((scope) => ({
        id: scope.id,
        title: scope.name,
        name: scope.name,
        description: scope.description,
        status: (KNOWN_STATUSES.includes(scope.status as ScopeStatus)
          ? (scope.status as ScopeStatus)
          : 'planned') as ScopeStatus,
        startupId: scope.startupId,
        createdAt: scope.createdAt,
        updatedAt: scope.updatedAt,
        iceScore: ice.scoresByScopeId[scope.id]?.ice ?? scope.iceScore ?? null,
      })),
    [list.scopes, ice.scoresByScopeId]
  );

  const requestDeleteScope = useCallback(
    (id: string) => {
      const scope = scopesWithIce.find((s) => s.id === id);
      if (scope) setScopeToDelete({ id: scope.id, title: scope.title });
    },
    [scopesWithIce]
  );

  const cancelDeleteScope = useCallback(() => setScopeToDelete(null), []);

  const confirmDeleteScope = useCallback(async () => {
    if (!scopeToDelete) return;
    await facade.deleteScope(scopeToDelete.id);
    setScopeToDelete(null);
    list.refetch();
  }, [scopeToDelete, facade, list]);

  /**
   * Open the details popup. The card is fetched on open rather than for every
   * row up front — the list only needs the composite, which the ranking
   * already carries in one call.
   */
  const openIceDetails = useCallback(
    (scopeId: string) => {
      setIceScopeId(scopeId);
      void ice.loadCard(scopeId);
    },
    [ice]
  );

  const closeIceDetails = useCallback(() => setIceScopeId(null), []);

  const iceDialog = useMemo(() => {
    if (!iceScopeId) {
      return { scopeId: null, scopeTitle: undefined, data: null, isLoading: false, isScoring: false, error: null };
    }
    return {
      scopeId: iceScopeId,
      scopeTitle: scopesWithIce.find((s) => s.id === iceScopeId)?.title,
      data: ice.cardsByScopeId[iceScopeId] ?? null,
      isLoading: ice.loadingCardScopeIds.includes(iceScopeId),
      isScoring: ice.scoringScopeIds.includes(iceScopeId),
      error: ice.errorsByScopeId[iceScopeId] ?? null,
    };
  }, [
    iceScopeId,
    scopesWithIce,
    ice.cardsByScopeId,
    ice.loadingCardScopeIds,
    ice.scoringScopeIds,
    ice.errorsByScopeId,
  ]);

  return {
    facade,
    ice,
    list,
    scopesWithIce,
    iceDialog,
    openIceDetails,
    closeIceDetails,
    scopeToDelete,
    requestDeleteScope,
    cancelDeleteScope,
    confirmDeleteScope,
  };
}
